/**
 * PsicoSalud — Listado y alta de pacientes (Módulo 2).
 *
 * Los datos identificables se cifran en el backend; aquí solo se
 * capturan y se muestran ya descifrados sobre HTTPS.
 */

import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { patientsApi } from '@/api';
import { ApiError } from '@/api/client';
import EmptyState, { IconPerson } from '@/components/EmptyState';
import Skeleton from '@/components/Skeleton';
import { useToast } from '@/context/ToastContext';
import type { PatientSummary } from '@/types';

const PAGE_SIZE = 25;

export default function PatientsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [patients, setPatients] = useState<PatientSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchDocument, setSearchDocument] = useState('');

  const [form, setForm] = useState({
    document_id: '',
    full_name: '',
    birth_date: '',
    gender: 'unknown',
    phone: '',
    email: '',
    emergency_contact: '',
    address: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const page = await patientsApi.list({ limit: PAGE_SIZE, offset });
      setPatients(page.pacientes);
      setTotal(page.total);
    } catch {
      toast({ type: 'error', message: t('errors.network') });
    } finally {
      setLoading(false);
    }
  }, [offset, t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSearch = async (event: FormEvent) => {
    event.preventDefault();
    if (!searchDocument.trim()) {
      setOffset(0);
      await load();
      return;
    }
    try {
      const patient = await patientsApi.searchByDocument(searchDocument.trim());
      setPatients([
        {
          id: patient.id,
          document_id: patient.document_id,
          full_name: patient.full_name,
          gender: patient.gender,
          is_active: patient.is_active,
          created_at: patient.created_at,
        },
      ]);
      setTotal(1);
    } catch {
      setPatients([]);
      setTotal(0);
      toast({ type: 'error', message: t('patients.notFound') });
    }
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await patientsApi.create({
        document_id: form.document_id,
        full_name: form.full_name,
        birth_date: form.birth_date || null,
        gender: form.gender,
        phone: form.phone || null,
        email: form.email || null,
        emergency_contact: form.emergency_contact || null,
        address: form.address || null,
      });
      toast({ type: 'success', message: t('patients.created') });
      setShowForm(false);
      setForm({
        document_id: '',
        full_name: '',
        birth_date: '',
        gender: 'unknown',
        phone: '',
        email: '',
        emergency_contact: '',
        address: '',
      });
      setOffset(0);
      await load();
    } catch (error) {
      const message =
        error instanceof ApiError && error.status === 409
          ? t('patients.duplicate')
          : error instanceof ApiError
            ? error.message
            : t('errors.generic');
      toast({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  const update = (field: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [field]: event.target.value }));

  return (
    <>
      <header className="page-header with-flex">
        <div>
          <span className="page-eyebrow">{t('nav.patients')}</span>
          <h1>{t('patients.title')}</h1>
          <p className="page-descripcion">
            {total} {total === 1 ? 'paciente registrado' : 'pacientes registrados'}
          </p>
        </div>
        <div className="page-acciones">
          <button
            onClick={() => setShowForm((open) => !open)}
            data-cy="patients-toggle-form"
          >
            {showForm ? t('common.cancel') : t('patients.newPatient')}
          </button>
        </div>
      </header>

      <div className="card">
        <div className="card-header">
          <span className="t-mayuscula">{t('common.search')}</span>
          <form
            onSubmit={handleSearch}
            style={{ display: 'flex', gap: '0.6rem', flex: 1, maxWidth: '420px' }}
          >
            <input
              type="text"
              placeholder={t('patients.searchByDocument')}
              value={searchDocument}
              onChange={(event) => setSearchDocument(event.target.value)}
              aria-label={t('patients.searchByDocument')}
              data-cy="patients-search-input"
            />
            <button type="submit" className="secondary" data-cy="patients-search-submit">
              {t('common.search')}
            </button>
          </form>
        </div>
      </div>

      {showForm && (
        <div className="card" data-cy="patients-form-card">
          <div className="card-header">
            <h2>{t('patients.newPatient')}</h2>
          </div>
          <form onSubmit={handleCreate} data-cy="patients-form">
            <div className="form-row">
              <div className="field">
                <label htmlFor="document_id">{t('patients.documentId')}</label>
                <input
                  id="document_id"
                  required
                  minLength={5}
                  value={form.document_id}
                  onChange={update('document_id')}
                  data-cy="patients-document"
                />
              </div>
              <div className="field">
                <label htmlFor="full_name">{t('patients.fullName')}</label>
                <input
                  id="full_name"
                  required
                  minLength={2}
                  value={form.full_name}
                  onChange={update('full_name')}
                  data-cy="patients-name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label htmlFor="birth_date">{t('patients.birthDate')}</label>
                <input id="birth_date" type="date" value={form.birth_date} onChange={update('birth_date')} />
              </div>
              <div className="field">
                <label htmlFor="gender">{t('patients.gender')}</label>
                <select id="gender" value={form.gender} onChange={update('gender')}>
                  <option value="female">{t('patients.genders.female')}</option>
                  <option value="male">{t('patients.genders.male')}</option>
                  <option value="other">{t('patients.genders.other')}</option>
                  <option value="unknown">{t('patients.genders.unknown')}</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label htmlFor="phone">{t('patients.phone')}</label>
                <input id="phone" value={form.phone} onChange={update('phone')} data-cy="patients-phone" />
              </div>
              <div className="field">
                <label htmlFor="email">{t('patients.email')}</label>
                <input id="email" type="email" value={form.email} onChange={update('email')} />
              </div>
            </div>

            <div className="field">
              <label htmlFor="emergency_contact">{t('patients.emergencyContact')}</label>
              <input
                id="emergency_contact"
                value={form.emergency_contact}
                onChange={update('emergency_contact')}
              />
            </div>

            <div className="field">
              <label htmlFor="address">{t('patients.address')}</label>
              <input id="address" value={form.address} onChange={update('address')} />
            </div>

            <div className="form-actions">
              <button type="button" className="secondary" onClick={() => setShowForm(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" disabled={submitting} data-cy="patients-submit">
                {t('common.save')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        {loading ? (
          <Skeleton.Table rows={6} columns={4} />
        ) : patients.length === 0 ? (
          <EmptyState
            icon={<IconPerson />}
            title={t('patients.emptyTitle')}
            description={t('patients.emptyDescription')}
            action={{
              label: t('patients.newPatient'),
              onClick: () => setShowForm(true),
            }}
          />
        ) : (
          <>
            <div className="table-wrapper">
              <table data-cy="patients-table">
                <thead>
                  <tr>
                    <th style={{ width: '10rem' }}>{t('patients.documentId')}</th>
                    <th>{t('patients.fullName')}</th>
                    <th style={{ width: '8rem' }}>{t('agenda.status')}</th>
                    <th style={{ width: '14rem', textAlign: 'right' }}>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {patients.map((patient) => (
                    <tr key={patient.id} data-cy="patients-row">
                      <td className="t-numerico">{patient.document_id}</td>
                      <td data-cy="patients-row-name">
                        <Link
                          to={`/pacientes/${patient.id}`}
                          style={{
                            color: 'var(--tinta)',
                            textDecoration: 'none',
                            fontWeight: 'var(--peso-medio)',
                          }}
                        >
                          {patient.full_name}
                        </Link>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            patient.is_active ? 'badge-success' : 'badge-neutral'
                          }`}
                        >
                          {patient.is_active ? t('common.yes') : t('patients.inactive')}
                        </span>
                      </td>
                      <td>
                        <div className="button-row is-final">
                          <Link
                            to={`/pacientes/${patient.id}`}
                            className="is-button is-secondary is-pequeno"
                            data-cy="patients-open"
                          >
                            {t('common.edit')}
                          </Link>
                          <Link
                            to={`/pacientes/${patient.id}/historial`}
                            className="is-button is-pequeno"
                          >
                            {t('record.title')}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              style={{
                marginTop: 'var(--espacio-4)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 'var(--espacio-3)',
              }}
            >
              <span className="t-mayuscula">
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} {t('common.of')} {total}
              </span>
              <div className="button-row">
                <button
                  className="secondary is-pequeno"
                  disabled={offset === 0}
                  onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
                  aria-label="Anterior"
                >
                  ←
                </button>
                <button
                  className="secondary is-pequeno"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => setOffset((value) => value + PAGE_SIZE)}
                  aria-label="Siguiente"
                >
                  →
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
